import { useMemo } from 'react';
import { AlertTriangle, Layers, TrendingUp, Truck } from 'lucide-react';
import { RankedBarChart, SeverityDonut, TrendChart } from '../charts/Charts';
import { ChartCard, StatTile } from './StatsParts';
import type { AlertEvent } from '../../types/alert';
import {
  alertTrend, alertsByCategory, alertsByVehicle, averageResolutionMinutes,
  formatMinutes, resolveRange,
} from '../../utils/dashboardStats';

interface AlertsStatsPanelProps {
  /** Ya recortadas al periodo por el Dashboard. */
  alerts: AlertEvent[];
  from?: string;
  to?: string;
  periodLabel: string;
}

export function AlertsStatsPanel({ alerts, from, to, periodLabel }: AlertsStatsPanelProps) {
  const stats = useMemo(() => {
    const { start, end } = resolveRange(from, to, alerts.map(a => a.timestamp));
    const avgMinutes = averageResolutionMinutes(alerts);
    return {
      trend: alertTrend(alerts, start, end),
      byCategory: alertsByCategory(alerts),
      byVehicle: alertsByVehicle(alerts, 6),
      open: alerts.filter(a => a.status === 'active').length,
      closed: alerts.filter(a => a.status === 'closed').length,
      critical: alerts.filter(a => a.severity === 'critical').length,
      medium: alerts.filter(a => a.severity === 'medium').length,
      low: alerts.filter(a => a.severity === 'low').length,
      avgMinutes,
    };
  }, [alerts, from, to]);

  const noAlerts = alerts.length === 0;

  return (
    <div className="dashboard-stats">
      <div className="dashboard-stats__tiles">
        <StatTile label="Alertas en el periodo" value={alerts.length} />
        <StatTile label="Abiertas" value={stats.open} hint="siguen activas" />
        <StatTile label="Cerradas" value={stats.closed} />
        <StatTile
          label="Resolución promedio"
          value={stats.avgMinutes === null ? '—' : formatMinutes(stats.avgMinutes)}
          hint={stats.avgMinutes === null ? 'ninguna cerrada aún' : 'de la alerta a su cierre'}
        />
      </div>

      {/* 50/50 (decisión de la reunión Mindor Dev Team, 2026-09-14). */}
      <section className="dashboard-page__charts">
        <ChartCard title={`Distribución por severidad — ${periodLabel}`} icon={<AlertTriangle size={18} />} empty={noAlerts}>
          <SeverityDonut critical={stats.critical} medium={stats.medium} low={stats.low} />
        </ChartCard>
        <ChartCard title={`Tendencia de alertas — ${periodLabel}`} icon={<TrendingUp size={18} />} empty={noAlerts}>
          <TrendChart data={stats.trend} />
        </ChartCard>
        <ChartCard title="Por categoría" icon={<Layers size={18} />} empty={noAlerts}>
          <RankedBarChart data={stats.byCategory} seriesName="Alertas" />
        </ChartCard>
        <ChartCard title="Vehículos con más alertas" icon={<Truck size={18} />} empty={stats.byVehicle.length === 0}>
          <RankedBarChart data={stats.byVehicle} seriesName="Alertas" />
        </ChartCard>
      </section>
    </div>
  );
}
